import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Eye, EyeOff, ArrowRight, ShieldCheck, Zap, Users, FileSpreadsheet } from "lucide-react";

// Pharmacy-themed hero photograph (stable CDN)
const HERO_IMG =
  "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=1800&q=80";
const SSSMT_LOGO =
  "https://customer-assets-agu9un31.emergentagent.net/job_medi-stock-hub/artifacts/oiq3o0bd_image.png";

// ---- Custom Brand Logo (pill + medical cross, emerald) ------------------
function MediCoreLogo({ className = "w-11 h-11" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="lg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#10B981" />
          <stop offset="1" stopColor="#059669" />
        </linearGradient>
      </defs>
      {/* rounded square background */}
      <rect x="1.5" y="1.5" width="45" height="45" rx="12" fill="url(#lg)" />
      {/* medical cross */}
      <path
        d="M24 12v24M12 24h24"
        stroke="white"
        strokeWidth="4.2"
        strokeLinecap="round"
      />
      {/* pill capsule diagonally overlaid */}
      <g transform="translate(24 24) rotate(45) translate(-24 -24)">
        <rect x="14" y="21" width="20" height="6" rx="3" fill="#FDE68A" opacity="0.95" />
        <rect x="14" y="21" width="10" height="6" rx="3" fill="#F59E0B" opacity="0.95" />
      </g>
    </svg>
  );
}

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  if (user && user !== false) {
    return <Navigate to={loc.state?.from?.pathname || "/"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) nav(loc.state?.from?.pathname || "/", { replace: true });
  };

  const features = [
    { icon: ShieldCheck, label: "Branch-isolated data" },
    { icon: Zap,         label: "Barcode-ready POS" },
    { icon: Users,       label: "Role-based access" },
    { icon: FileSpreadsheet, label: "PDF / XLSX exports" },
  ];

  return (
    <div className="min-h-screen grid md:grid-cols-[1.15fr_520px] bg-slate-50" data-testid="login-page">
      {/* ---------------- Left visual pane ---------------- */}
      <div className="hidden md:block relative overflow-hidden">
        {/* hero photo */}
        <img
          src={HERO_IMG}
          alt="Pharmacy interior with rows of medicine bottles"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* dark emerald + navy gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#052e2b]/85 via-[#04322d]/70 to-[#002FA7]/70" />
        {/* soft radial spotlight */}
        <div
          className="absolute inset-0 opacity-70 mix-blend-screen"
          style={{
            backgroundImage:
              "radial-gradient(650px circle at 25% 30%, rgba(16,185,129,0.35), transparent 60%), radial-gradient(500px circle at 80% 80%, rgba(59,130,246,0.25), transparent 55%)",
          }}
        />
        {/* subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        {/* Floating pill motifs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-24 right-16 w-28 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 rotate-[20deg] animate-[float_9s_ease-in-out_infinite]" />
          <div className="absolute bottom-32 left-20 w-20 h-8 rounded-full bg-emerald-300/20 backdrop-blur border border-white/10 -rotate-[15deg] animate-[float_11s_ease-in-out_infinite_reverse]" />
          <div className="absolute top-[45%] right-[35%] w-16 h-16 rounded-full border-2 border-white/15" />
        </div>

        {/* content */}
        <div className="relative p-10 xl:p-14 h-full flex flex-col justify-between text-white">
          {/* Brand row */}
          <div className="flex items-center gap-3">
            <MediCoreLogo className="w-12 h-12 drop-shadow-lg" />
            <div>
              <div className="font-display font-bold text-2xl leading-none">MedOne</div>
              <div className="text-[11px] opacity-80 mt-1.5 tracking-[0.28em] uppercase">
                Pharmacy Management System
              </div>
            </div>
          </div>

          {/* Hero copy */}
          <div className="max-w-lg space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur px-3 py-1 text-[11px] tracking-[0.25em] uppercase text-emerald-100">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
              Control tower for modern pharmacies
            </div>
            <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[1.02] tracking-tight">
              Stock. Sales. Staff.
              <br />
              <span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-white bg-clip-text text-transparent">
                One calm dashboard.
              </span>
            </h1>
            <p className="text-white/80 text-base leading-relaxed max-w-md">
              Barcode-ready POS, expiry-aware stock, salt-composition matching, and reports that
              print themselves — designed for multi-branch pharmacies that need clarity, not clutter.
            </p>

            {/* Feature chips */}
            <div className="grid grid-cols-2 gap-3 max-w-lg pt-2">
              {features.map((f) => (
                <div
                  key={f.label}
                  className="flex items-center gap-2.5 rounded-xl bg-white/8 border border-white/12 backdrop-blur px-3.5 py-2.5 text-sm text-white/90"
                >
                  <f.icon className="w-4 h-4 text-emerald-300 shrink-0" />
                  <span className="truncate">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom trust stats + developer credit */}
          <div className="space-y-6">
            <div className="flex gap-8 text-xs text-white/60">
              <div>
                <div className="text-2xl font-bold text-white leading-none">24/7</div>
                <div className="mt-1.5 uppercase tracking-widest">Real-time sync</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white leading-none">Multi</div>
                <div className="mt-1.5 uppercase tracking-widest">Branch isolated</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white leading-none">GST</div>
                <div className="mt-1.5 uppercase tracking-widest">GSTR-1 / 3B ready</div>
              </div>
            </div>

            {/* SSSMT / ISDE developer credit */}
            <div
              className="flex items-start gap-4 rounded-xl bg-white/8 border border-white/12 backdrop-blur px-4 py-3 max-w-xl"
              data-testid="developer-credit"
            >
              <div className="shrink-0 w-14 h-14 rounded-lg bg-white p-1.5 flex items-center justify-center">
                <img src={SSSMT_LOGO} alt="Shri Suvakumar Singh Memorial Trust"
                     className="w-full h-full object-contain" />
              </div>
              <div className="text-[11px] leading-snug text-white/85">
                MIS Developed by <b className="text-white">Institute of Skill Development and Entrepreneurship (ISDE), Manipur</b>
                <div className="text-white/60 mt-0.5">
                  An Institute run under Shri Suvakumar Singh Memorial Trust (SSSMT)
                </div>
                <div className="text-white/60 mt-1">
                  <a href="mailto:sssmt.tbl@gmail.com" className="hover:text-emerald-300 underline underline-offset-2">
                    sssmt.tbl@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------- Right form pane ---------------- */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          {/* Mobile brand */}
          <div className="md:hidden flex items-center gap-3 mb-10">
            <MediCoreLogo className="w-10 h-10" />
            <div>
              <div className="font-display font-bold text-lg leading-none">MedOne</div>
              <div className="text-[10px] tracking-widest uppercase text-slate-500 mt-1">
                Pharmacy MIS
              </div>
            </div>
          </div>

          <div className="overline mb-2">Welcome back</div>
          <h2 className="font-display text-3xl font-bold mb-2">Sign in to continue</h2>
          <p className="text-sm text-slate-500 mb-8">
            Use your branch credentials to access the portal.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                placeholder="you@yourpharmacy.com"
                className="w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-500 transition-all"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  className="w-full border border-slate-200 rounded-lg px-3.5 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-500 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-semibold py-2.5 text-[15px] transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                "Signing in..."
              ) : (
                <>
                  Sign in <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-10 border-t border-slate-100 pt-5">
            <div className="overline text-[10px] mb-1.5">Need access?</div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Login IDs are created by your <b>administrator</b>. Each branch has its own staff and
              inventory — sign in with your branch credentials.
            </p>
          </div>

          <div className="mt-8 text-[10px] text-slate-400 tracking-widest uppercase text-center">
            HIPAA-conscious · Encrypted · Branch-isolated
          </div>

          {/* Developer credit — visible on mobile where left pane is hidden */}
          <div className="md:hidden mt-6 flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
            <img src={SSSMT_LOGO} alt="SSSMT"
                 className="w-10 h-10 object-contain shrink-0" />
            <div className="text-[10px] leading-snug text-slate-600">
              MIS Developed by <b>Institute of Skill Development and Entrepreneurship (ISDE), Manipur</b>
              <div className="text-slate-500 mt-0.5">Run under Shri Suvakumar Singh Memorial Trust (SSSMT)</div>
              <a href="mailto:sssmt.tbl@gmail.com" className="text-emerald-700 underline underline-offset-2 mt-0.5 inline-block">sssmt.tbl@gmail.com</a>
            </div>
          </div>
        </div>
      </div>

      {/* Floating pill keyframes — scoped via arbitrary Tailwind */}
      <style>{`
        @keyframes float {
          0%,100% { transform: translateY(0) rotate(20deg); }
          50%     { transform: translateY(-14px) rotate(24deg); }
        }
      `}</style>
    </div>
  );
}
